import { JSX } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Label, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from "recharts";
import { CategoricalChartState } from "recharts/types/chart/types";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { ProviderCountData, ProviderCountTypeEnum } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { colors } from "@app/overviews/network-benchmarking-dashboard/tiles/utility";

interface ProviderCountProps {
  canDrillIn: boolean;
  customTooltip: (
    props: TooltipProps<ValueType, string | number>,
    titleKey: string,
  ) => JSX.Element;
  data: ProviderCountData[]
  onDashboardDetailsViewClick: (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    isGhost?: boolean,
  ) => void;
  providerCountType: ProviderCountTypeEnum;
}

const ProviderCount = (props: ProviderCountProps): JSX.Element => {
  const getCellFill = (inOtherNetworkCount: string): string => {
    if (props.providerCountType === ProviderCountTypeEnum.InNetwork) {
      if (inOtherNetworkCount === "Unique") {
        return colors.green;
      } else {
        return colors.cyan;
      }
    } else {
      return colors.lightBlue;
    }
  }

  const onProviderCountClick = (data: CategoricalChartState): void => {
    // only allow to drill into the dashboard details view when
    // clicking in a bar section and the ability to drill into the dashboard is enabled
    if (!!data && props.canDrillIn) {
      const providerCountData: ProviderCountData = props.data.find(x => x.inOtherNetworkCount === data.activeLabel);

      if (!!providerCountData?.count) {
        if (data.activeLabel === "Unique") {
          props.onDashboardDetailsViewClick(
            BenchmarkingDashboardDetailsTypeEnum.VennDiagramPrimaryOnly,
            providerCountData.count,
          );
        } else {
          props.onDashboardDetailsViewClick(
            props.providerCountType === ProviderCountTypeEnum.InNetwork ? BenchmarkingDashboardDetailsTypeEnum.OverlapInNetwork : BenchmarkingDashboardDetailsTypeEnum.OverlapOutNetwork,
            providerCountData.count,
            null,
            parseInt(providerCountData.inOtherNetworkCount),
          );
        }
      }
    }
  }

  return (
    <div>
      {props.providerCountType === ProviderCountTypeEnum.InNetwork ? (
        <div className="row">
          <div className="col-md-12">
            <label className="label-m mt-3 mb-0 d-block">
              Primary Network Provider Participation Across Comparison Networks
            </label>

            <label className="copy-text-default d-block mb-3 text-muted">
              Displays the number of providers in the primary network that are also shared with one or more comparison networks. This shows how many primary network providers are shared with comparison networks, from unique to widely contracted.
            </label>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-md-12">
            <label className="label-m mt-3 mb-0 d-block">
              Providers Participating Only in Comparison Networks
            </label>

            <label className="copy-text-default d-block mb-0 text-muted">
              Shows the number of providers who are not in the selected primary network, broken down by how many other networks they participate in. This helps identify providers actively contracted only in the comparison networks, highlighting potential opportunities for contracting.
            </label>
          </div>
        </div>
      )}
      {!!props.data && (
        <div style={{ height: "300px", width: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              barCategoryGap="20%"
              width={500}
              height={300}
              data={props.data}
              margin={{
                bottom: 15, // formatting to prevent the bottom label from truncating
                left: 0,
                right: 0,
                top: 20,
              }}
              onClick={(data: CategoricalChartState) => onProviderCountClick(data)}
            >
              <CartesianGrid horizontal={false} vertical={false} />
              <XAxis dataKey="inOtherNetworkCount" type="category" tickLine={false}>
                <Label value={`# of ${props.providerCountType === ProviderCountTypeEnum.OutOfNetwork ? "Comparison " : ""}Networks Provider Is In`} offset={-10} position="insideBottom" />
              </XAxis>
              <YAxis dataKey="count" type="number" tickLine={false} />
              <Tooltip content={(customTooltipProps) => props.customTooltip(customTooltipProps, "contractId")} />
              <Bar dataKey="count">
                {props.data.map((entry, index) => (
                  // custom bar chart colors
                  <Cell id={`benchmarking-dashboard__provider-count-${props.providerCountType?.replace(/ /g, "")}--network-${entry.inOtherNetworkCount}-count-${entry.count}`} key={`cell-${index}`} fill={getCellFill(entry.inOtherNetworkCount)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

export default ProviderCount;
